import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DisplayImage from '@/components/Ui-components/display-image';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, overlay, fontSize, fontWeight, transition} from '@/styles/tokens';
import {AWARD_CARDS, type AwardCard} from '@/constants/badges';
import {maxWidth, NARROW_LAYOUT_MAX} from '@/styles/breakpoints';

// Below 1500px the whole block steps down — the same breakpoint the hero and
// section headings already use.
const NARROW = maxWidth(NARROW_LAYOUT_MAX);

const GAP = 20;
const AUTOPLAY_MS = 1600;
const RESET_MS = 320;

// Matches the `cardSx.flex` breakpoints below, so the JS-measured "how many cards
// are on screen" (used for the translateX step and the coverflow centre) always
// agrees with what the CSS is actually rendering.
const getVisibleCount = (width: number) => {
    if (width < 600) return 1;
    if (width < 900) return 3;
    return 5;
};

// ─── sx constants ─────────────────────────────────────────────────────────────
const sectionSx = {
    width: '100%',
    background: colors.primary.deep,
    py: {xs: 5, md: 7},
    [NARROW]: {py: 4}
} as const;

const frameSx = {
    borderRadius: borderRadius['3xl'],
    border: '1px solid rgba(255,255,255,0.18)',
    background: colors.primary.navy,
    px: {xs: 2.5, md: 6},
    py: {xs: 3, md: 4},
    [NARROW]: {px: 4, py: 2.5}
} as const;

const headingSx = {
    textAlign: 'center' as const,
    color: colors.text.inverse,
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[32], md: fontSize[46]},
    mb: 1,
    [NARROW]: {fontSize: fontSize[36], mb: 0.75}
} as const;

const headingAccentSx = {color: colors.text.inverse} as const;

const subheadingSx = {
    textAlign: 'center' as const,
    color: 'rgba(255,255,255,0.6)',
    fontSize: fontSize[15],
    // Wide enough to hold the sentence on a single line on desktop; it still wraps on
    // narrow screens, where one line would not fit at any sensible size.
    maxWidth: 'none',
    mx: 'auto',
    mb: 4,
    [NARROW]: {fontSize: fontSize[14], mb: 3}
} as const;

const trackRowSx = {display: 'flex', alignItems: 'center', gap: 2} as const;

// `py` is the room the scaled-up centre card and its glow need to render without
// being clipped; `my` pulls most of it back, leaving a little real breathing space
// above and below the row.
const trackViewportSx = {overflow: 'hidden', flex: 1, py: 5, my: -3, [NARROW]: {py: 4, my: -2.5}} as const;

const trackSx = {
    display: 'flex',
    alignItems: 'stretch',
    gap: `${GAP}px`
} as const;

// A structured tile — framed logo, divider, then name/category — so it flows
// top-to-bottom and needs a real height rather than a 1:1 aspect ratio.
const cardSx = {
    flex: {
        xs: '0 0 100%',
        sm: `0 0 calc((100% - ${GAP * 2}px) / 3)`,
        md: `0 0 calc((100% - ${GAP * 4}px) / 5)`
    },
    minWidth: 0,
    height: 300,
    [NARROW]: {height: 250},
    display: 'flex',
    flexDirection: 'column',
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    transformOrigin: 'center center',
    transition: `transform ${transition.slow}, opacity ${transition.slow}, background ${transition.slow}, box-shadow ${transition.slow}`
} as const;

// The focused (centre) card is scaled up at full opacity; every other card sits
// smaller and dimmed, so the eye is drawn straight to it.
// `scaleUp` is off on phones: there only one card is visible, so scaling the focused one
// past the track's width pushed its own text outside the viewport, where the track's
// `overflow: hidden` clipped it.
const getCoverflowStyle = (distance: number, scaleUp: boolean) => {
    const abs = Math.abs(distance);
    if (abs === 0) {
        return {
            transform: scaleUp ? 'scale(1.15)' : 'scale(1)',
            zIndex: 2,
            opacity: 1,
            background: colors.background.default,
            boxShadow: 'none'
        };
    }
    if (abs === 1) {
        return {transform: 'scale(0.92)', zIndex: 1, opacity: 0.55, background: colors.background.default, boxShadow: 'none'};
    }
    return {transform: 'scale(0.78)', zIndex: 0, opacity: 0.3, background: colors.background.default, boxShadow: 'none'};
};

const getArrowBtnSx = () =>
    ({
        width: 44,
        height: 44,
        [NARROW]: {width: 38, height: 38},
        flexShrink: 0,
        borderRadius: borderRadius.circle,
        border: `1px solid ${overlay.whiteLight}`,
        color: colors.text.inverse,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: `border-color ${transition.slow}, background ${transition.slow}`,
        '&:hover': {borderColor: colors.text.inverse, background: overlay.whiteSubtle}
    } as const);

const focusedNameSx = {
    textAlign: 'center' as const,
    color: colors.text.inverse,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[18],
    mt: 4,
    [NARROW]: {fontSize: fontSize[16], mt: 2.5}
} as const;

const progressTrackSx = {
    maxWidth: 420,
    mx: 'auto',
    mt: 2,
    height: 3,
    borderRadius: borderRadius.full,
    background: overlay.whiteTenth,
    overflow: 'hidden'
} as const;

// Autoplay fills linearly over the whole interval; an arrow click fills in RESET_MS, as fast
// as the cards slide, so the bar keeps up with clicks and completes before a loop reset.
const getProgressFillSx = (widthPercent: number, animated: boolean, durationMs: number) =>
    ({
        height: '100%',
        borderRadius: borderRadius.full,
        background: colors.accent.orangeDark,
        width: `${widthPercent}%`,
        transition: animated ? `width ${durationMs}ms linear` : 'none'
    } as const);

const captionSx = {
    textAlign: 'center' as const,
    color: 'rgba(255,255,255,0.5)',
    fontSize: fontSize[13],
    mt: 1.5,
    [NARROW]: {fontSize: fontSize[12], mt: 1}
} as const;

// ─── Card-internal sx ─────────────────────────────────────────────────────────

const yearPillSx = {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 1,
    px: 1.25,
    py: 0.25,
    borderRadius: borderRadius.full,
    bgcolor: colors.background.tag,
    color: colors.primary.deep,
    fontSize: fontSize[12],
    fontWeight: fontWeight.semibold
} as const;

// No boxed/tinted background behind the badge — it sits directly on the card's
// own (white) surface, just padded and with room for the year pill above it.
const imageFrameSx = {
    position: 'relative',
    // Takes whatever height the body does not need, rather than a fixed 75% — on
    // shorter cards the fixed split clipped the second line of the category.
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 1.5,
    pt: 2.5,
    pb: 1
} as const;

const dividerSx = {borderTop: `1px solid ${colors.neutral.borderLight}`} as const;

const cardBodySx = {
    px: 1.5,
    py: 1.25,
    flex: 'none',
    minWidth: 0,
    [NARROW]: {px: 1.25, py: 1}
} as const;

const cardTitleSx = {
    color: colors.text.primary,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    lineHeight: 1.35,
    [NARROW]: {fontSize: fontSize[15]}
} as const;

const cardCategorySx = {
    color: colors.text.secondary,
    fontSize: fontSize[13],
    lineHeight: 1.35,
    mt: 0.25,
    [NARROW]: {fontSize: fontSize[12]},
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
} as const;
// ─────────────────────────────────────────────────────────────────────────────

// Loop copies are decorative: hidden from assistive tech and unfocusable (`inert`; React 18
// has no typed prop for it, so it goes through as a plain attribute).
const cloneAttrs = {'aria-hidden': true, inert: ''} as Record<string, unknown>;

const DEFAULT_SUBHEADING = 'Independent recognition across Web3, blockchain, AI, smart contracts, tokenization, and software development.';

interface CertificationsSectionProps {
    /** Page-specific badge set. Defaults to the full list; at least five keep the carousel full. */
    cards?: AwardCard[];
    subheading?: string;
}

const CertificationsSection = ({cards = AWARD_CARDS, subheading = DEFAULT_SUBHEADING}: CertificationsSectionProps) => {
    const total = cards.length;
    const tripled = useMemo(() => [...cards, ...cards, ...cards], [cards]);
    const [index, setIndex] = useState(total);
    const [animated, setAnimated] = useState(true);
    const [paused, setPaused] = useState(false);
    // True after an arrow click, until autoplay takes the next step.
    const [manual, setManual] = useState(false);
    // Bumped on every arrow click to restart the autoplay timer, so autoplay never fires a
    // step straight after the visitor's own.
    const [navNonce, setNavNonce] = useState(0);
    const resetting = useRef(false);
    const [visible, setVisible] = useState(5);
    const sectionRef = useRef<HTMLDivElement>(null);
    // The page wraps this section in `content-visibility: auto`, which skips paint
    // (not JS) while it's off-screen. Without this, the autoplay interval keeps
    // ticking in the background, and the section jumps several cards ahead the
    // instant it scrolls into view instead of animating continuously. Pausing the
    // interval while off-screen keeps it running smoothly the moment it's visible.
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const measure = () => setVisible(getVisibleCount(globalThis.innerWidth));
        measure();
        globalThis.addEventListener('resize', measure);
        return () => globalThis.removeEventListener('resize', measure);
    }, []);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {threshold: 0.1});
        io.observe(node);
        return () => io.disconnect();
    }, []);

    // Each badge is exposed to assistive tech once: through its on-screen card if it has one, otherwise
    // through the middle copy. Cards on screen (plus one either side, mid-slide) are never hidden.
    const isHiddenCopy = (i: number) => {
        const inViewport = (j: number) => j >= index - 1 && j <= index + visible;
        if (inViewport(i)) return false;
        const item = i % total;
        const onScreenElsewhere = [item, item + total, item + total * 2].some((j) => j !== i && inViewport(j));
        return onScreenElsewhere || i < total || i >= total * 2;
    };

    const centerOffset = Math.floor(visible / 2);
    const stepPercent = 100 / visible;
    const logicalIndex = (((index + centerOffset - total) % total) + total) % total;
    const focused = cards[logicalIndex];
    const cycleStep = (((index - total) % total) + total) % total;
    // Each tick aims the bar at the end of its step; the width transition matches the autoplay
    // interval, so it fills continuously and lands on 100% as the last badge's step ends. On the
    // wrap tick (track on the duplicate copy) it holds at 100% instead of shrinking back, and
    // while the silent reset runs (transitions off) it drops to 0% — so the next cycle visibly
    // starts from empty rather than snapping from ~97% to 10%.
    const isWrapping = index >= total * 2;
    const isRestarting = !animated && cycleStep === 0;
    const progressPercent = isWrapping ? 100 : isRestarting ? 0 : ((cycleStep + 1) / total) * 100;

    const next = useCallback(() => {
        if (resetting.current) return;
        setIndex((i) => i + 1);
    }, []);
    const prev = useCallback(() => {
        if (resetting.current) return;
        setIndex((i) => i - 1);
    }, []);
    // Silent reset once the index drifts into a duplicate copy, so the loop never
    // visibly jumps.
    useEffect(() => {
        let t: ReturnType<typeof setTimeout> | undefined;
        if (index >= total * 2) {
            resetting.current = true;
            t = setTimeout(() => {
                setAnimated(false);
                setIndex(index - total);
            }, RESET_MS + 20);
        } else if (index < total) {
            resetting.current = true;
            t = setTimeout(() => {
                setAnimated(false);
                setIndex(index + total);
            }, RESET_MS + 20);
        }
        return () => {
            if (t) clearTimeout(t);
        };
    }, [index, total]);

    useEffect(() => {
        if (!animated) {
            let r2: number;
            const r1 = requestAnimationFrame(() => {
                r2 = requestAnimationFrame(() => {
                    setAnimated(true);
                    resetting.current = false;
                });
            });
            return () => {
                cancelAnimationFrame(r1);
                if (r2) cancelAnimationFrame(r2);
            };
        }
        return undefined;
    }, [animated]);

    useEffect(() => {
        if (paused || !inView) return undefined;
        const timer = setInterval(() => {
            setManual(false);
            next();
        }, AUTOPLAY_MS);
        return () => clearInterval(timer);
    }, [paused, inView, next, navNonce]);

    const handleNext = () => {
        setManual(true);
        setNavNonce((n) => n + 1);
        next();
    };
    const handlePrev = () => {
        setManual(true);
        setNavNonce((n) => n + 1);
        prev();
    };

    return (
        <Box ref={sectionRef} sx={sectionSx}>
            <MUIContainer>
                <Box sx={frameSx}>
                    <Typography component="h2" className="font-headers" sx={headingSx}>
                        Badges &amp;{' '}
                        <Box component="span" sx={headingAccentSx}>
                            Recognition
                        </Box>
                    </Typography>
                    <Typography component="p" className="font-regular-text" sx={subheadingSx}>
                        {subheading}
                    </Typography>

                    <Box sx={trackRowSx} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                        <Box sx={getArrowBtnSx()} onClick={handlePrev} role="button" aria-label="Previous">
                            <ArrowBackIosNewIcon sx={{fontSize: fontSize[16]}} />
                        </Box>

                        <Box sx={trackViewportSx}>
                            <Box
                                sx={{
                                    ...trackSx,
                                    transform: `translateX(calc(-${index} * (${stepPercent}% + ${GAP}px / ${visible})))`,
                                    transition: animated ? `transform ${transition.slow}` : 'none'
                                }}
                            >
                                {tripled.map((item, i) => (
                                    <Box
                                        key={`${item.name}-${i}`}
                                        {...(isHiddenCopy(i) ? cloneAttrs : {})}
                                        sx={{...cardSx, ...getCoverflowStyle(i - index - centerOffset, visible > 1)}}
                                    >
                                        <Box sx={imageFrameSx}>
                                            <Box sx={yearPillSx}>{item.year}</Box>
                                            <DisplayImage
                                                src={item.src}
                                                alt={item.alt}
                                                width={160}
                                                height={160}
                                                quality={100}
                                                style={{objectFit: 'contain', maxWidth: '100%', maxHeight: '100%'}}
                                            />
                                        </Box>
                                        <Box sx={dividerSx} />
                                        <Box sx={cardBodySx}>
                                            <Typography component="p" className="font-headers" sx={cardTitleSx}>
                                                {item.name}
                                            </Typography>
                                            <Typography component="p" className="font-regular-text" sx={cardCategorySx}>
                                                {item.category}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box sx={getArrowBtnSx()} onClick={handleNext} role="button" aria-label="Next">
                            <ArrowForwardIosIcon sx={{fontSize: fontSize[16]}} />
                        </Box>
                    </Box>

                    <Typography component="p" className="font-headers" sx={focusedNameSx}>
                        {focused.category}
                    </Typography>

                    <Box sx={progressTrackSx}>
                        <Box sx={getProgressFillSx(progressPercent, animated, manual ? RESET_MS : AUTOPLAY_MS)} />
                    </Box>

                    <Typography sx={captionSx}>
                        {focused.name} · {focused.category} · {focused.region} · {focused.year}
                    </Typography>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(CertificationsSection);
